import { useMemo, useState } from "react";
import { categories, posts } from "@/mocks/content";
import SectionHeading from "@/components/feature/SectionHeading";
import { badgeTint, catOf, textAccent } from "./helpers";

const ALL = "all";

export default function Columns() {
  const [active, setActive] = useState<string>(ALL);
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return posts.filter((p) =>
        [p.title, p.summary, ...p.tags, catOf(p).name].some((s) => s.toLowerCase().includes(q))
      );
    }
    if (active === ALL) return posts;
    return posts.filter((p) => p.cat === active);
  }, [active, query]);

  return (
    <section id="columns" className="w-full bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="五大吃瓜栏目"
          title="吃瓜瓜棚 · 热讯不断"
          desc="瓜友求锤、预言挖坟、脱粉回踩、站姐现场、业内信源——多路瓜讯聚合，任你挑选想听的那一口。"
        />

        {/* control bar */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* tabs */}
          <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-background-200 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => setActive(ALL)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === ALL
                  ? "bg-foreground-950 text-background-50"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-layout-grid-line" aria-hidden="true" />
              全部瓜讯
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActive(c.id)}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === c.id
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className={c.icon} aria-hidden="true" />
                {c.name}
              </button>
            ))}
          </div>

          {/* search */}
          <div className="relative w-full max-w-md shrink-0">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索瓜讯标题、标签、栏目…"
              aria-label="搜索瓜讯"
              className="w-full rounded-full border border-background-200 bg-background-50 py-2.5 pl-10 pr-10 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="清空搜索"
                className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 hover:text-primary-600"
              >
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {/* result count */}
        <p className="mt-5 text-sm text-foreground-500">
          {query ? (
            <>
              关于“<span className="font-semibold text-primary-600">{query}</span>”找到{" "}
              <span className="font-semibold text-foreground-900">{list.length}</span> 条瓜讯
            </>
          ) : (
            <>
              共 <span className="font-semibold text-foreground-900">{list.length}</span> 条瓜讯 · 每日持续更新
            </>
          )}
        </p>

        {/* cards */}
        {list.length > 0 ? (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p) => (
              <article
                key={p.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-background-300"
              >
                <a href="#guide" className="block overflow-hidden" aria-label={p.title}>
                  <div className="relative">
                    <img
                      src={p.img}
                      alt={p.title}
                      title={`${p.title} - 每日大瓜`}
                      className="aspect-[16/10] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className={`absolute left-3 top-3 rounded-md px-2.5 py-1 text-xs font-bold ${badgeTint(p.cat)}`}>
                      {catOf(p).name}
                    </span>
                  </div>
                </a>
                <div className="flex flex-1 flex-col p-4">
                  <p className={`text-xs font-semibold ${textAccent(p.cat)}`}>{p.kicker}</p>
                  <h3 className="mt-1.5 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                    <a href="#guide" className="transition-colors">
                      {p.title}
                    </a>
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{p.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <span key={t} className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600">
                        #{t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-eye-line" aria-hidden="true" /> {p.reads}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-chat-3-line" aria-hidden="true" /> {p.comments.toLocaleString()}
                    </span>
                    <span className="text-foreground-500">{p.date}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-primary-400">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有找到相关瓜讯</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词，或试试其他栏目试试看</p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setActive(ALL);
              }}
              className="mt-5 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              清空并查看全部
            </button>
          </div>
        )}
      </div>
    </section>
  );
}